/*
 * Purpose: Apply OutcomePlan to Mission map — empty flow replace or existing flow merge
 * Last modified: 2026-07-10 | Agent
 * Completeness: 95
 */
import type { Workflow } from '@/stores/workflowStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { OutcomePlan } from '@/services/autoblocks/types';
import {
  assembleWorkflowFromPlan,
  mergeWorkflowRefinement,
} from '@/services/autoblocks/workflowAssembler';
import { planFromOutcomeAsync } from '@/services/autoblocks/planFromOutcomeAsync';
import { invalidateFlowSummaryCache } from '@/services/autoblocks/flowSummary';
import { buildGoalCentricMapContext } from '@/lib/mapAssistantContext';
import { getMissionGoalIntent } from '@/lib/mapNodeSummary';

/** Flow has no real automation yet — only anchors and/or placeholder manual+log. */
export function isMinimalMissionFlow(workflow: Workflow | null | undefined): boolean {
  if (!workflow) return true;

  const operational = workflow.nodes.filter(
    (n) =>
      n.type === 'trigger' ||
      n.type === 'tool' ||
      n.type === 'action' ||
      n.type === 'agent' ||
      n.type === 'drone'
  );

  if (operational.length === 0) return true;

  if (operational.length <= 2) {
    const signatures = operational
      .map((n) => `${n.type}.${n.subtype}`)
      .sort()
      .join('|');
    if (signatures === 'action.log|trigger.manual') return true;
  }

  return false;
}

export function preserveWorkflowIdentity(
  assembled: Workflow,
  current: Workflow | null | undefined
): Workflow {
  if (!current) return assembled;
  return {
    ...assembled,
    id: current.id,
    createdAt: current.createdAt,
    name: assembled.name?.trim() ? assembled.name : current.name,
    missionMeta: { ...current.missionMeta, ...assembled.missionMeta },
  };
}

export interface ApplyPlanToMapOptions {
  /** When true, merge with current positions/ids instead of full replace */
  refine?: boolean;
  /** Persist to tenant storage when subscription allows (default true) */
  persist?: boolean;
  /** Expected workflow id — abort if user switched flows mid-plan */
  expectedWorkflowId?: string | null;
}

export interface ApplyPlanToMapResult {
  applied: boolean;
  workflow: Workflow | null;
  isRefinement: boolean;
}

/** Assemble plan → merge if refining → apply to current map (always updates canvas). */
export function applyPlanToMap(
  plan: OutcomePlan,
  options?: ApplyPlanToMapOptions
): ApplyPlanToMapResult {
  const store = useWorkflowStore.getState();
  const current = store.currentWorkflow;
  const expectedId = options?.expectedWorkflowId;

  if (expectedId && current?.id !== expectedId) {
    return { applied: false, workflow: null, isRefinement: false };
  }

  const minimal = isMinimalMissionFlow(current);
  const isRefinement = options?.refine ?? (!minimal && Boolean(current));

  let assembled = assembleWorkflowFromPlan(plan, current?.name);
  assembled = preserveWorkflowIdentity(assembled, current);

  if (isRefinement && current) {
    assembled = mergeWorkflowRefinement(current, assembled);
    assembled = preserveWorkflowIdentity(assembled, current);
  }

  const goalOutcome = (plan.rawOutcome || plan.interpretedGoal || '').trim();
  if (goalOutcome) {
    assembled = {
      ...assembled,
      nodes: assembled.nodes.map((node) => {
        if (node.type !== 'goal' || node.subtype !== 'outcome') return node;
        const existing = String(node.config.outcome ?? '').trim();
        return {
          ...node,
          config: {
            ...node.config,
            outcome: existing || goalOutcome,
          },
        };
      }),
    };
  }

  const applied = store.applyAssembledWorkflow(assembled, {
    persist: options?.persist !== false,
  });

  if (applied && assembled.id) {
    invalidateFlowSummaryCache(assembled.id);
  }

  return {
    applied,
    workflow: applied ? useWorkflowStore.getState().currentWorkflow : null,
    isRefinement,
  };
}

/** Plan from goal text then apply to the current map. */
export async function planAndApplyGoalToMap(
  outcomeText: string,
  options?: ApplyPlanToMapOptions & { mapContext?: string }
): Promise<ApplyPlanToMapResult & { plan: OutcomePlan }> {
  const trimmed = outcomeText.trim();
  if (!trimmed) {
    throw new Error('Describe the outcome you want this Mission to accomplish.');
  }

  const current = useWorkflowStore.getState().currentWorkflow;
  const mapContext = options?.mapContext ?? buildGoalCentricMapContext(current);

  const plan = await planFromOutcomeAsync(trimmed, { mapContext });
  const result = applyPlanToMap(plan, options);
  return { ...result, plan };
}

/** Read GOAL block / flow name and wire suggested blocks onto the map. */
export async function wireGoalIntentToMap(
  options?: ApplyPlanToMapOptions
): Promise<ApplyPlanToMapResult & { plan?: OutcomePlan }> {
  const current = useWorkflowStore.getState().currentWorkflow;
  const intent = getMissionGoalIntent(current);
  if (!intent || intent.includes('Describe your outcome')) {
    throw new Error('Set the GOAL outcome first — what should this Mission accomplish?');
  }

  const { plan, ...result } = await planAndApplyGoalToMap(intent, options);
  return { ...result, plan };
}
